import {Box, Flex, Stack, useTheme, Text} from '@chakra-ui/react';
import React, {useEffect, useMemo, useState} from 'react';
import dayjs from 'dayjs';
import {Place, Event, Post, Offer} from '../../types/article';
import ItemCarousel from '../ItemCarousel';
import {
  TextA14,
  TextA16,
  TextA18,
  TextA20,
  TextG12,
  TextG14,
  TextG16,
  TextG18,
} from '../typography';
import {useNavigate, useSearchParams} from 'react-router-dom';
import PlaceCard from '../Articles/PlaceCard';
import EventCard from '../Articles/EventCard';
import PostCard from '../Articles/PostCard';
import {SkeletonLoader} from '../SkeletonLoader';
import useGetUserLocation from '../../hooks/useUserLocation';
import {usePlaceTypes} from '../../hooks/usePlaceTypes';
import {getAppName} from '../../utils/app';
import OfferCard from '../Articles/Offers/OfferCard';
import TutorialTooltips from '../TutorialTooltips';
import useScrollPosition from '../../hooks/useScrollPosition';
import useActiveRedemptions from '../../hooks/useActiveRedemptions';
import {useFetchOffer, useOffers, useMonthlyPerksSummary} from '../../hooks/useOffers';
import {useEvents} from '../../hooks/useEvents';
import {useInfinitePlacesByType} from '../../hooks/useFetchPlacesByType';
import {useInfiniteGlobalSearchWithParamsAndLocation} from '../../hooks/useFetchGlobalSearch';
import {filter, map, uniqBy} from 'lodash';
import {getArticleTopItems} from '../../utils/articleTopItems';
import HomeTooltips from '../TutorialTooltips/HomeTooltips';
import DiscoverMoreArrow from '../HomeContext/DiscoverMoreArrow';
import HomeQuickLinks from '../HomeQuickLinks/index';
import {useFeaturedContents} from '../../hooks/useFeaturedContents';

// Import our new components
type FeaturedContent = Post | Place | Offer | Event;
import {useSelector} from 'react-redux';
import GuidedExperienceWidget from '../GuidedExperienceWidget/GuidedExperienceWidget';
import useAuth from '../../hooks/useAuth';
import HomeTop from '../TopBar/HomeTop';

import PerksButtonDarkAlt from '../MemberPerksHub/PerksButtonDarkAlt';
import InsiderUpsellCard from '../../pages/MyProfile/components/InsiderUpsellCard';
import IndividualRedemptionsCarousel from './IndividualRedemptionsCarousel';

const HomeFeed = ({showLoader}: {showLoader?: boolean}) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const userLocation = useGetUserLocation();
  const {user} = useAuth();
  const {placeTypesLoading, getIdsForCategory} = usePlaceTypes();
  const [searchParams] = useSearchParams();
  const paramOfferId = searchParams.get('offer');
  const [showTutorial, setShowTutorial] = useState(false);
  const [homeOffers, setHomeOffers] = useState<Array<any>>([]);
  const paramOffer = useFetchOffer(paramOfferId);
  const isTutorial = !!searchParams.get('tutorial');
  const selectedCommunityId = useSelector((state: any) => state.community.community)?.id;

  const isProduction = process.env.NODE_ENV === 'production';
  const insiderClubInterestId = isProduction ? '294' : '212';

  const featuredContentComponents = useMemo(() => {
    return {
      post: <PostCard />,
      event: <EventCard />,
      place: <PlaceCard />,
      offer: <OfferCard />,
    };
  }, []);

  const {activeRedemptions} = useActiveRedemptions();

  const {events, eventsIsLoading} = useEvents({communityId: selectedCommunityId?.toString()});
  const {offers, offersIsLoading} = useOffers({communityId: selectedCommunityId?.toString()});

  // Fetch monthly perks summary for button display
  const {data: perksSummary, isLoading: perksSummaryLoading} = useMonthlyPerksSummary();

  const currentMonthRetailValue = perksSummary?.data?.current_month?.total_retail_value ?? 0;
  const nextMonthRetailValue = perksSummary?.data?.next_month?.total_retail_value ?? 0;
  const currentMonthCount = perksSummary?.data?.current_month?.count ?? 0;
  const nextMonthCount = perksSummary?.data?.next_month?.count ?? 0;

  const fetchMemberOffers = useInfiniteGlobalSearchWithParamsAndLocation({
    type: 'offer',
    lat: userLocation?.data?.latitude,
    long: userLocation?.data?.longitude,
    'topics[]': insiderClubInterestId,
    term: '',
    orderBy: 'ends_at',
    sort: {by: 'ends_at', direction: 'asc'},
    month: 'current',
  });
  const memberOffers = fetchMemberOffers.data?.pages.flatMap((page) => page.data.data) || [];

  const fetchNextMonthOffers = useInfiniteGlobalSearchWithParamsAndLocation({
    type: 'offer',
    lat: userLocation?.data?.latitude,
    long: userLocation?.data?.longitude,
    'topics[]': insiderClubInterestId,
    term: '',
    orderBy: 'ends_at',
    sort: {by: 'ends_at', direction: 'asc'},
    month: 'next',
  });
  const nextMonthOffers = fetchNextMonthOffers.data?.pages.flatMap((page) => page.data.data) || [];

  const {
    featuredContents,
    isFeaturedContentsLoading,
  }: {featuredContents: FeaturedContent[]; isFeaturedContentsLoading: boolean} =
    useFeaturedContents({communityId: selectedCommunityId?.toString()});

  useEffect(() => {
    if (offers) {
      const activeOffers = map(
        filter(activeRedemptions, (redemption) => !!redemption.offer.countdown_required),
        (redemption) => redemption.offer,
      );
      setHomeOffers((prev) => {
        return uniqBy([...offers, ...activeOffers], 'id');
      });
    }
  }, [offers]);

  const foodPlaceIds: string[] = useMemo(() => {
    if (!placeTypesLoading) {
      return [
        ...getIdsForCategory('Places to eat'),
        ...getIdsForCategory('Bars'),
        ...getIdsForCategory('Coffee & Desserts'),
      ];
    } else return [];
  }, [placeTypesLoading]);

  const fetchFoodPlaces = useInfinitePlacesByType({
    lat: userLocation?.data?.latitude,
    long: userLocation?.data?.longitude,
    open: 'open',
    typeIds: foodPlaceIds,
    sort: 'ai',
    communityId: selectedCommunityId?.toString(),
    enabled: !placeTypesLoading,
  });
  const foodPlaces = fetchFoodPlaces.data?.pages[0]?.data?.data;

  const isLoading =
    eventsIsLoading ||
    offersIsLoading ||
    fetchFoodPlaces.isLoading ||
    isFeaturedContentsLoading ||
    placeTypesLoading ||
    showLoader;

  useScrollPosition(!isTutorial);

  useEffect(() => {
    if (!!searchParams.get('offer') && homeOffers.length > 0 && paramOffer) {
      const offer = paramOffer.data?.data;
      const offerId = offer?.id;
      if (offerId && filter(homeOffers, {id: offerId}).length == 0 && offer) {
        setHomeOffers((prev) => {
          if (offer && filter(prev, {id: offer.id}).length == 0) return [offer, ...prev];
          return [...prev];
        });
      }
    }
  }, [searchParams.get('offer'), homeOffers, paramOffer]);

  useEffect(() => {
    setShowTutorial(isTutorial);

    if (isTutorial) window.scrollTo(0, 0);
  }, [searchParams]);
  return (
    <Box>
      <>
        <HomeTop />
        {showTutorial && <TutorialTooltips tooltipFunc={HomeTooltips} />}

        {user?.hasActiveMembership ? (
          perksSummaryLoading ? (
            <SkeletonLoader mt="34px" mb="12px" cardBorderRadius="12px" />
          ) : (
            <PerksButtonDarkAlt
              offers={memberOffers || []}
              nextMonthOffers={nextMonthOffers || []}
              perksCount={currentMonthCount}
              nextMonthPerksCount={nextMonthCount}
              totalRetailValue={currentMonthRetailValue}
              nextMonthRetailValue={nextMonthRetailValue}
              user={user}
            />
          )
        ) : (
          <Box mt="45px" />
        )}

        <IndividualRedemptionsCarousel communityId={selectedCommunityId?.toString()} />

        <HomeQuickLinks />

        {/* Non-member Premium Membership CTA */}
        {!user?.hasActiveMembership && (
          <Flex
            onClick={() => navigate('/memberships')}
            borderRadius="12px"
            background={theme.colors.rose}
            p={4}
            mt="1.5rem !important"
            mb="1.5rem !important"
            justifyContent="space-between"
            alignItems="center"
            cursor="pointer"
            _hover={{opacity: 0.95}}
            _active={{transform: 'scale(0.99)'}}>
            <Box>
              <TextG18 fontWeight="700" color="white">
                Check Out Premium Membership
              </TextG18>
              <TextG16 fontWeight="400" color="white" mt={0.5}>
                Next-Level Connection & Belonging
              </TextG16>
            </Box>
            <Box as="span" color="white" fontSize="24px" display="flex" alignItems="center">
              <i className="fi fi-rr-angle-small-right" />
            </Box>
          </Flex>
        )}

        <DiscoverMoreArrow />

        <Stack>
          {fetchFoodPlaces.isLoading ? (
            <SkeletonLoader mt="34px" cardBorderRadius="12px" />
          ) : (
            <ItemCarousel
              id="feed-tooltip"
              highlightId="feed-tooltip-highlight"
              title="Explore Places Nearby"
              topRightItem={
                <TextA14
                  id="more_places"
                  fontWeight="900"
                  onClick={() =>
                    navigate(
                      `/search/place?categories=${encodeURIComponent(
                        foodPlaceIds.join(','),
                      )}&sort=ai`,
                    )
                  }
                  color="#276ACD">
                  More
                </TextA14>
              }
              swiperProps={{
                autoHeight: true,
                items: foodPlaces,
                render: (place) => (
                  <PlaceCard
                    isSlide
                    place={place as Place}
                    {...getArticleTopItems({...place, type: 'place'})}
                  />
                ),
              }}
            />
          )}

          {offersIsLoading ? (
            <SkeletonLoader mt="34px" cardBorderRadius="12px" />
          ) : (
            <>
              <ItemCarousel
                id="feed2-tooltip"
                highlightId="feed2-tooltip-highlight"
                title="Get 'Locals Only' Perks"
                topRightItem={
                  <TextA14
                    id="more_offers"
                    fontWeight="900"
                    onClick={() => navigate('/search/offer')}
                    color="#276ACD">
                    More
                  </TextA14>
                }
                swiperProps={{
                  autoHeight: true,
                  slidesPerGroup: 1,
                  slidesPerView: 1,
                  items: homeOffers,
                  render: (item) => {
                    return <OfferCard offer={item as Offer} />;
                  },
                }}
              />
            </>
          )}

          {eventsIsLoading ? (
            <SkeletonLoader mt="34px" cardBorderRadius="12px" />
          ) : (
            <ItemCarousel
              id="home-rsvp-tooltip"
              highlightId="rsvp-tooltip-highlight"
              title="Go To Events Nearby"
              topRightItem={
                <TextA14
                  id="more_events"
                  fontWeight="900"
                  onClick={() => navigate('/search/event')}
                  color="#276ACD">
                  More
                </TextA14>
              }
              swiperProps={{
                autoHeight: true,
                items: events,
                render: (event) => (
                  <EventCard
                    isSlide
                    event={event as Event}
                    {...getArticleTopItems({...event, type: 'event'})}
                  />
                ),
              }}
            />
          )}

          {/* <GuidedExperienceWidget /> */}

          {/* Premium Membership Widget for Non-Members */}
          {!user?.hasActiveMembership && <InsiderUpsellCard mx={0} mb={0} my="1.5rem !important" />}

          <Stack textAlign="center" px="15%">
            <TextG12 fontWeight="500">You're all caught up for now!</TextG12>
            <TextG12
              color={theme.colors.grey}
              onClick={() => navigate('/profile/lifestyle-goals-and-interests')}>
              Personalize your {getAppName()} experience by adding more interest tags.
              <i className="fi fi-rr-add" style={{position: 'relative', left: '5px', top: '2px'}} />
            </TextG12>
          </Stack>
        </Stack>
      </>
    </Box>
  );
};

export default HomeFeed;
